import { Injectable, OnDestroy } from '@angular/core';
import { NS_Enums, NS_SubscriptionService, customerSupportLink, newPaymentDetails, newPricingRoute, trialStarterConfigureDestination, trialStarterConfigureSource, trialStarterHeading } from "src/app/shared/namespaces";
import { SubscriptionDataService } from "./../../account/services/subscription-data.service"
import { BehaviorSubject, Observable, Subject, forkJoin, of, throwError, scheduled, asyncScheduler } from "rxjs";
import { catchError, mergeMap, retry, take, takeUntil, tap } from "rxjs/operators";
import { capitalize, cloneDeep, filter, find, get, head, includes, isArray, isEmpty, isNil, isUndefined, keys, omit, orderBy, size } from "lodash-es";
import { UserDetails } from "src/app/app.model";
import { CompanySubscription } from "./../../account/models/subscription-models";
import { AppService } from "src/app/app.service";
import { formatDateCustom, getDaysCount, getEndDateMidgnightUTC, getStartDateMidnightUTC, isJsonValid } from 'src/app/shared/helpers/helpers';
import { BannerConfig } from 'src/app/shared/components/banner/banner.component';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { CommonDialogComponent } from 'src/app/common-dialog/common-dialog.component';
import { CacheService } from 'src/app/cache.service';
import { Router } from '@angular/router';
import { environment } from 'src/environments/environment';
import { isIqSetupActive } from 'src/app/shared/helpers/iq-setup-context.helper';


export interface trialPlanEventsSubjectData { eventKey:trialPlanEventsKeyEnum, eventValue:any }

export enum trialPlanEventsKeyEnum {
    'isTrialPlanActive' = 'isTrialPlanActive',
    'trialExpiryMessage' = 'trialExpiryMessage',
    'trialExpiryDays' = 'trialExpiryDays',
    'isTrialPlanUsed' = 'isTrialPlanUsed',
    'initial' = 'initial'
}

export interface trialEventParams {
  isTrialPlanActive:boolean;
  isTrialPlanUsed:boolean;
  trialMessage:string;
  bannerConfig?: BannerConfig;
}

@Injectable({
  providedIn: 'root'
})
export class TrialPlanService implements OnDestroy {
  baseWord: any;

  constructor(private subscriptionDataService: SubscriptionDataService, private appService: AppService, private dialog: MatDialog,private cacheService: CacheService, private router: Router ) {
    this.baseWord = this.appService.getBaseWord()
   }
  //isTrialCompanySubscriptionCreated = false;
  private _expiryDays: any;

  public get expiryDays() {
    return this._expiryDays;
  }

  public set expiryDays(value: any) {
    this._expiryDays = value;
  } 

  doWhenNewUserSignUpReported(userEmail){
    this.userSignup.update({isNewUserSignup : true, userEmail : userEmail});
  }
  userSignup = {
    isNewUserSignup : false,
    userEmail: '',
    update : (obj: {isNewUserSignup: boolean, userEmail: string })=>{
      this.userSignup.isNewUserSignup = obj.isNewUserSignup;
      this.userSignup.userEmail = obj.userEmail;
    }
  };
  private serviceDestroyed: Subject<any> = new Subject();
  trialPlanEventsSubject = new BehaviorSubject<trialPlanEventsSubjectData>({eventKey: trialPlanEventsKeyEnum.initial, eventValue: -1});
  ngOnDestroy(): void {
    this.destroy();
  }

  destroy(){
    this.userDetails = undefined;
    this.userSignup.update({isNewUserSignup : false, userEmail : ''});
    //this.isTrialCompanySubscriptionCreated = false;
    sessionStorage.removeItem(NS_Enums.sessionStorageKeysEnum.isTrialSetupStepOneShown);
    sessionStorage.removeItem(NS_Enums.sessionStorageKeysEnum.isTrialSetupStepTwoShown);
    this._companySubscription = undefined;
    this.companySubscriptions = [];
    this._isTrialPlanActive = false;
    this._trialExpiryMessage = '';
    this._isTrialPlanUsed = false;
    this._expiryDays = undefined;
    this.trialPlanEventsSubject.next({eventKey: trialPlanEventsKeyEnum.initial, eventValue:  -1});
    this.trialPlanEventsSubject.complete();
    this.serviceDestroyed.next();
    this.serviceDestroyed.complete();
  }
  
  private _isTrialPlanUsed = false;

  public get isTrialPlanUsed() {
    return this._isTrialPlanUsed;
  }
  private _isTrialPlanActive:boolean=false;

  public get isTrialPlanActive() {
    return this._isTrialPlanActive;
  }

  private _trialExpiryMessage: string;

  public get trialExpiryMessage() {
    return this._trialExpiryMessage;
  }

  get isNewTrialGuidanceInProgress(){
    return !this.isTrialPlanActive && !this.isTrialPlanUsed
  }
  
  private _companySubscription: CompanySubscription;
  private companySubscriptions: any = [];
  private getTrialCompanySubscription(){
    return cloneDeep(this._companySubscription) ;
  }
  private setTrialCompanySubscription(companySubscription: CompanySubscription){
    this._companySubscription = companySubscription;
    this.trialPlanEventsSubject.next({eventKey:trialPlanEventsKeyEnum.trialExpiryDays, eventValue: getDaysCount(companySubscription.nextBillingDate, new Date().getTime()) });
  }

  // use this function to set multiple trial state at once
  private setTrialState(obj: trialEventParams){
    if(obj.isTrialPlanActive !== undefined){
      this._isTrialPlanActive = obj.isTrialPlanActive;
      const additionalProperties = omit(obj, keys(trialPlanEventsKeyEnum))
      this.trialPlanEventsSubject.next({eventKey:trialPlanEventsKeyEnum.isTrialPlanActive, eventValue:obj.isTrialPlanActive, ...additionalProperties});
    }
    if(obj.isTrialPlanUsed !== undefined){
      this._isTrialPlanUsed = obj.isTrialPlanUsed;
      const additionalProperties = omit(obj, keys(trialPlanEventsKeyEnum))
      this.trialPlanEventsSubject.next({eventKey:trialPlanEventsKeyEnum.isTrialPlanUsed, eventValue:obj.isTrialPlanUsed, ...additionalProperties});
      
    }
    if(obj.trialMessage !== undefined){
      this._trialExpiryMessage = obj.trialMessage
      const additionalProperties = omit(obj, keys(trialPlanEventsKeyEnum))
      this.trialPlanEventsSubject.next({eventKey:trialPlanEventsKeyEnum.trialExpiryMessage, eventValue:obj.trialMessage, ...additionalProperties});
    }
  }

  private trialSubscriptionPlan;
  get isOldActiveUser(){
    return get(this.oldSubscription,'status','') === "ENABLED" && !includes(["", "trial"], get(this.oldSubscription,'planId','')) &&  get(this.oldSubscription,'subscriptionPlanId') !== 0 ;
  }

  private get isOldTrialUser(){
    return get(this.oldSubscription,'status','') == "ENABLED" && includes([ "trial"], get(this.oldSubscription,'planId','')) ;
  }

  private userDetails;
  private oldSubscription;
  private productName:string;
  init( eventsSubject: Subject<NS_SubscriptionService.eventsSubjectData>, userDetails: UserDetails, productName: string){
    this.userDetails = userDetails;
    this.productName = productName;
    if(this.trialPlanEventsSubject.isStopped){
      this.trialPlanEventsSubject = new BehaviorSubject<trialPlanEventsSubjectData>({eventKey: trialPlanEventsKeyEnum.initial, eventValue: -1});
    }
    // from(this.appService.getCurrentSubscriptionPlan(this.userDetails.userId))
    forkJoin([this.getOldSubscriptionPlan().pipe(take(1)), this.getTrialSubscriptionPlan(), this.getCompanySubscriptions()]).subscribe({
      next: (data)=>{
        let result = {oldSubscription : data[0], trialSubscriptionPlan : data[1], companySubscriptions : data[2]};
        console.log('trialService: Init Completed', );
        this.doWhenCompanySubscriptionsReceived(result.companySubscriptions);
      },
      error: (err:any)=>{
        console.log('failed at forkjoin in trialplanservice init:', err);
      }
    });
    eventsSubject.pipe(takeUntil(this.serviceDestroyed)).subscribe((data: NS_SubscriptionService.eventsSubjectData) => {
      if(data.eventKey === NS_SubscriptionService.eventSubjectDataEventKeyEnum.companySubscriptions){
          this.doWhenCompanySubscriptionsReceived(data.eventValue);
      }
    });

    this.appService.getWarehouseBannerEvent().pipe(takeUntil(this.serviceDestroyed)).subscribe({
      next: (load) => {
        if(!load){
          // this will be used when new destination is created, we show source creation step2 when dstination created for first time.
          console.log('received this.appService.getWarehouseBannerEvent in trial plan service');
          this.doWhenTrialExistAndNotActive();
        }
      }
    });

  }

  private getTrialSubscriptionPlan(){
    return this.subscriptionDataService.subscriptionPlan
      .getTrialSubscriptionPlan()
      .pipe(
        take(1),
        tap(plan => { if (plan) this.trialSubscriptionPlan = plan; }),
        catchError(error => {
          console.error('Error fetching trial subscription plan:', error);
          return throwError(() => error);
        })
      );
  }

  private getCompanySubscriptions(){
    return this.subscriptionDataService.companySubscription
      .getCompanySubscriptionByCompanyId(this.userDetails?.company?.companyId)
      .pipe(
        take(1),
        catchError(error => {
          console.error('Error fetching company subscriptions:', error);
          return throwError(() => error);
        })
      );
  }

  getOldSubscriptionPlan(){
    return scheduled(this.appService.getCurrentSubscriptionPlan(this.userDetails?.userId), asyncScheduler)
      .pipe(
        take(1),
        tap(data => { if (data) this.oldSubscription = data.body; }),
        mergeMap(data => data ? of(data.body) : of(null)),
        catchError(error => {
          console.error('Error fetching old subscription plan:', error);
          return throwError(() => error);
        })
      );
  }

  checkUserSubscriptionMigratedAfterRunningUtility(){
    return new Observable((observer) => {
      this.getOldSubscriptionPlan().pipe(
        mergeMap((oldSubscription) => {
          if(oldSubscription.userSubscriptionMigrated === true){
            return of(true);
          } else {
            
            return throwError(() => 'Error!');
          }
        }),
        retry(2) // retry 2 times on error
      ).pipe(take(1)).subscribe({
        next: (isUserMigrated) => {
          console.log('checkUserSubscriptionMigratedAfterRunningUtility: Migrated!!');
          observer.next(isUserMigrated);
        },
        error: (err) => {
          console.log('checkUserSubscriptionMigratedAfterRunningUtility: not migrated, Retried 2 times then quit!..');
          this.showBannerOnMigrationFailed();
          observer.error();
        }
      });
    })
  }

  showBannerOnMigrationFailed(){
    this.setTrialState({ isTrialPlanActive: this.isTrialPlanActive, isTrialPlanUsed: this.isTrialPlanUsed, trialMessage:'User Migration failed, Contact Support', bannerConfig: {
      theme: 'error',
      title: 'User Migration failed',
      buttonText: 'Talk to customer support',
      buttonUrl: customerSupportLink
    }})
  }

  
  activateTrialCompanySubscriptionIfApplicable(){
    if(this._isTrialPlanUsed === false && this._isTrialPlanActive === false){
        this.activateTrialCompanySubscription();
    }
  }

  endTrialCompanySubscriptionIfApplicable(){
    if(this._isTrialPlanUsed === false ){
      this.endTrialCompanySubscription();
    }
  }

  // private createTrialCompanySubscription(){
  //     // do not call this method as PUT user call this while signup 
  //     let create = () => {
  //         let companySubscription = {
  //             "id": 0,
  //             "created": 0,
  //             "updated": 0,
  //             "companyId": this.userDetails.company.companyId,
  //             "subscriptionPlan": this.trialSubscriptionPlan,
  //             "status": NS_SubscriptionService.companySubscriptionStatusEnums.TRIAL,
  //             "chargebeeCustomerId": "",
  //             "chargebeeSubscriptionId": "",
  //             "nextBillingDate": 0, //need to pass 0 to identify this plan as new signup.
  //             "trialStarts": 0,
  //             "trialEnds": 0,
  //             "cycleStartDate": 0,
  //             "gracePeriod": 0,
  //             "overageBillingStartDate": 0,
  //             "overageBillingEndDate": 0
  //           }
  //           console.log('saveCompanySubscription', companySubscription );
  //         this.subscriptionDataService.companySubscription.saveCompanySubscription(companySubscription).subscribe({
  //             next: (data:any) => {
  //                 console.log('Trial subscription created', data);
  //                 this.setTrialCompanySubscription(data);
  //                 this.doWhenTrialExistAndNotActive();
  //             },
  //             error: (error) => {
  //                 console.log('Trial subscription creation failed', error);
  //             }
  //         });
  //     }
      
  //     if(!this.trialSubscriptionPlan){
  //         this.getTrialSubscriptionPlan().toPromise().then((data)=>{
  //             if(data){
  //                 create();
  //             }
  //         });
  //     } else{
  //         create();
  //     }
  // }

  private activateTrialCompanySubscription(){
    let compsub = this.getTrialCompanySubscription();
    if(!isUndefined(compsub) && !isUndefined(compsub.cycleStartDate) && !isUndefined(compsub.nextBillingDate) && get(compsub, ['subscriptionPlan','billingDays'], 0) > 0){
      this.subscriptionDataService.companySubscription.startTrial(compsub.id).subscribe({
        next: (response:any) => {
          let trialCompanySubscription = get(response, 'data');
          let errors = get(response, 'errors', []);
          if(!isUndefined(trialCompanySubscription) && isEmpty(errors)){
            this.setTrialCompanySubscription(trialCompanySubscription);
            this.doWhenTrialExistAndActive();
          } else if(!isEmpty(errors)){
            console.log('Start Trial Company subscription failed', errors);
            this.handleErrorDontRedirect({statusText: 'Start Trial Company subscription failed'});
          }
        }, error: (err)=>{
          console.log('Start Trial Company subscription failed', err);
         // this.handleErrorDontRedirect(err);
        }
      });
    }
  }

  private endTrialCompanySubscription(){
    let compsub = this.getTrialCompanySubscription();
    if(!isUndefined(compsub) && !isUndefined(compsub.cycleStartDate) && !isUndefined(compsub.nextBillingDate)){
      compsub.nextBillingDate = new Date().getTime();
      compsub.status = NS_SubscriptionService.companySubscriptionStatusEnums.TRIAL_ENDED;
      this.subscriptionDataService.companySubscription.updateCompanySubscription(compsub.id, compsub ).subscribe({
          next: (data:any) => {
            this.setTrialCompanySubscription(compsub);
            this.doWhenTrialExpiredAndPlanSubscribed();
          }
      });
    }
  }

  getCompanySubscriptionsSortByIdDesc(companySubscriptions ): any{
    // return company subscriptions in descending order of id desc
    let arr: any = JSON.parse(JSON.stringify(companySubscriptions));
    return orderBy(arr, ['id'], ['desc'])
  }

  get isEnforceMigrationCase(){
    return size(this.companySubscriptions) === 0 && (this.isOldActiveUser || this.isOldTrialUser) && this.oldSubscription.userSubscriptionMigrated === false && this.oldSubscription.enforceMigration == true
  }
  
  private doWhenCompanySubscriptionsReceived(companySubscriptions){
    this.companySubscriptions = companySubscriptions;
    // if old subscription is active and next billing date is in future then tell about new plans
    let trialCompanySub = find(companySubscriptions, (compSubscription) => includes(['Trial',  NS_SubscriptionService.trialSubscriptionPlanName], compSubscription?.subscriptionPlan?.name ) );
    if( this.userSignup.isNewUserSignup === false && get(this.oldSubscription,'status','') === "ENABLED" && isNil(trialCompanySub)  ){
      let message = '';
      if( includes(["", "trial"], get(this.oldSubscription,'planId',''))){
        if(this.oldSubscription.trialEnds === 0 ||
          this.oldSubscription.trialStarts === 0) { 
            
        }
        else if(this.oldSubscription.userSubscriptionMigrated === false){
          const now = (new Date()).getTime();
          const trialEnds = this.oldSubscription.trialEnds;
          const difference = trialEnds - now;
          if(difference >= 0){
            let expireDays = Math.round(((((difference/1000)/60)/60)/24))
            // this.ifTrial = true;
            if(expireDays === 0 || expireDays === 1){
              message = `Your trial expires today.`;
            } else {
              message = `Your trial expires in ${expireDays} days.`;
            }
          } else {
            message = `Your trial plan has expired.`;
          }
          //FIXME below bannerconfig can be removed after all users are migrated to new subscription.
          this.setTrialState({ isTrialPlanActive:false, isTrialPlanUsed:false, trialMessage: message,  bannerConfig: {
            theme: 'warning',
            title: message,
            description: `New plans will be available soon for ${capitalize(this.productName)}!`,
            buttonText: 'Check upcoming pricing plans',
            buttonUrl: environment.marketingPricingPageLink
          }});
        }
      } else {

        if (get(this.oldSubscription,'nextBillingDate')  === 0){
          //TODO below code is commented out because there are legacy users who are in enterprise plan with next billing date as 0.
          // for active plan status is 2.
          //message = `Your subscription plan is expired.`;
        } else  {
          //if(this.oldSubscription.userSubscriptionMigrated === false)
          if(get(this.oldSubscription,'nextBillingDate') > new Date().getTime() ){
            message = `Your subscription plan expires on ${formatDateCustom(get(this.oldSubscription,'nextBillingDate',0))}.`
          } else if (get(this.oldSubscription,'nextBillingDate')  > 0 && get(this.oldSubscription,'nextBillingDate') < new Date().getTime() ){
            message = `Your subscription plan expired on ${formatDateCustom(get(this.oldSubscription,'nextBillingDate',0))}.`
          }
          //FIXME below bannerconfig can be removed after all users are migrated to new subscription.

          // this.setTrialState({ isTrialPlanActive:false, isTrialPlanUsed:false, trialMessage: message,  bannerConfig: {
          //   theme: 'warning',
          //   title: message,
          //   description: `New plans are available in ${capitalize(this.productName)}!`,
          //   buttonText: 'Compare plans',
          //   buttonUrl: newPricingRoute
          // }});
        }
      }
    } else {
      // handle new subcription flow

      companySubscriptions = this.getCompanySubscriptionsSortByIdDesc(companySubscriptions);
      let latestCompSub:any = head(companySubscriptions);

      let isMigrationAnomaly = size(companySubscriptions) > 0 && (this.isOldActiveUser && this.oldSubscription.userSubscriptionMigrated === false );

      if(latestCompSub.status === NS_SubscriptionService.SUSPENDED ){
        this.doWhenCompanySusbcriptionIsSuspended(latestCompSub);
      } else if(isMigrationAnomaly){
        this.doWhenMigrationAnomalyFound();
      } else {
        let nonTrialCompanySubscriptionsCount = filter(companySubscriptions, (compSubscription) => !includes(['Trial',  NS_SubscriptionService.trialSubscriptionPlanName], compSubscription?.subscriptionPlan?.name )).length;
        if(trialCompanySub){
          this.setTrialCompanySubscription(trialCompanySub);
        }

        //const isEligibleForTrialCreation = !this.isTrialCompanySubscriptionCreated && !trialCompanySub && nonTrialCompanySubscriptionsCount == 0;
        const whenTrialNotExistAndPlanSubscribed = !trialCompanySub && nonTrialCompanySubscriptionsCount >= 1;
        const whenTrialExistAndNotActive = ( trialCompanySub
          && trialCompanySub.nextBillingDate === 0                 
          && trialCompanySub.status === NS_SubscriptionService.companySubscriptionStatusEnums.TRIAL 
          && nonTrialCompanySubscriptionsCount === 0);
        const whenTrialExistAndActive = (trialCompanySub
          && trialCompanySub.nextBillingDate >= new Date().getTime()
          && trialCompanySub.status === NS_SubscriptionService.companySubscriptionStatusEnums.TRIAL 
          && nonTrialCompanySubscriptionsCount === 0); 

        const whenTrialExpiredAndPlanNotSubscribed = (trialCompanySub
          && trialCompanySub.status === NS_SubscriptionService.companySubscriptionStatusEnums.TRIAL_ENDED
          && nonTrialCompanySubscriptionsCount === 0)

        const whenTrialExpiredAndPlanSubscribed = (trialCompanySub
          && trialCompanySub.status === NS_SubscriptionService.companySubscriptionStatusEnums.TRIAL_ENDED
          && nonTrialCompanySubscriptionsCount >= 1)


        // if (isEligibleForTrialCreation){
        //   this.isTrialCompanySubscriptionCreated = true;
        //   this.createTrialCompanySubscription();
        // }  
        
        if (whenTrialNotExistAndPlanSubscribed){
          this.doWhenTrialNotExistAndPlanSubscribed();
        } else if(whenTrialExistAndNotActive) {
          this.doWhenTrialExistAndNotActive();
        } else if(whenTrialExistAndActive){
          this.doWhenTrialExistAndActive();
        } else if(whenTrialExpiredAndPlanNotSubscribed){
          this.doWhenTrialExpiredAndPlanNotSubscribed();
        } else if(whenTrialExpiredAndPlanSubscribed){
          this.doWhenTrialExpiredAndPlanSubscribed();
        }
      }
    }

    if( this.isEnforceMigrationCase){
      let message = 'Attention!';
      this.setTrialState({ isTrialPlanActive:false, isTrialPlanUsed:false, trialMessage: message,  bannerConfig: {
        theme: 'error',
        title: message,
        description: `Please upgrade to new pricing plan to continue using ${capitalize(this.productName)}!`,
        buttonText: 'Check pricing plans',
        buttonUrl: newPricingRoute
      }});
    }
  }

  private shouldSuppressTrialUi(): boolean {
    return isIqSetupActive();
  }

  private doWhenTrialExistAndNotActive(){
    console.log('doWhenTrialExistAndNotActive entered' )
    this.setTrialState({ isTrialPlanActive:false, isTrialPlanUsed:false, trialMessage: ''});
    if (this.shouldSuppressTrialUi()) {
      return;
    }
    if(!isNil(this.userDetails?.userId)){
      scheduled(this.appService.getEnabledWarehouseList(this.userDetails?.userId, 0), asyncScheduler).pipe(take(1),catchError((err)=>{
        return throwError(err);
      })).subscribe({
        next: (activeWarehouseList)=>{
          console.log('activeWarehouseList.length == 0' , activeWarehouseList.length == 0);
          if(isArray(activeWarehouseList) ){
            if(activeWarehouseList.length === 0){
              if(this.userSignup.isNewUserSignup === false){
                this.openCreateDestinationPopUp();
                // this.setTrialState({ isTrialPlanActive:false, isTrialPlanUsed:false, trialMessage: message, bannerConfig: {
                //   theme: 'info',
                //   title: message,
                //   buttonText: 'Create Destination',
                //   buttonUrl: `/${this.baseWord}/connectors/datawarehouses-listing`
                // }})
              }
            } else if(activeWarehouseList.length > 0){
              this.doWhenActiveSourcesAreEmpty();
            }
          }
        },
        error:(err)=>{
          this.handleErrorDontRedirect(err);
        }
      })
    }
  }

  private doWhenActiveSourcesAreEmpty(){
    scheduled(this.appService.getActiveSources(this.userDetails?.userId), asyncScheduler).pipe(take(1),catchError((err)=>{
      return throwError(err);
    })).subscribe({
      next: (activeSourcesList)=>{
        console.log('activeSourcesList.length === 0' , activeSourcesList.length === 0);
        if(isArray(activeSourcesList) ){
          if(activeSourcesList.length === 0){
           
            this.openCreateSourcePopUp();
            // this.setTrialState({ isTrialPlanActive:false, isTrialPlanUsed:false, trialMessage: '', bannerConfig: {
            //   theme: 'info',
            //   title: message,
            //   buttonText: 'Create Source',
            //   buttonUrl: `/${this.baseWord}/connectors/datasources-listing`
            // }})
          }
        }
      },
      error:(err)=>{
        this.handleErrorDontRedirect(err);
      } 
    })
  }

  private openCreateDestinationPopUp(){
    if (this.shouldSuppressTrialUi()) {
      return;
    }
    let isTrialSetupStepOneShown = this.getIsTrialSetupStepShown('isTrialSetupStepOneShown') // JSON.parse(sessionStorage.getItem(NS_Enums.sessionStorageKeysEnum.isTrialSetupStepOneShown)) 
    if(!isTrialSetupStepOneShown){
      sessionStorage.setItem(NS_Enums.sessionStorageKeysEnum.isTrialSetupStepOneShown, JSON.stringify(true));
      const dialogRef = this.dialog.open(CommonDialogComponent, {
        data: {
          title: trialStarterHeading,
          content: trialStarterConfigureDestination ,
          image: "successful",
          type: 'trialFlow',
          stepSection: [
            {
              text: 'Configure destination',
              status: 'current',
              colorCss: 'bg-blue'
            }, 
            {
              text: 'Configure source',
              status: 'next',
              colorCss: 'bg-gray-200'
            }
          ],
          buttons: [
            {
              title: "Configure now",
              meta: "/connectors/datawarehouses-listing",
              type: "redirect",
              class: "confirm-dialog-later",
            },
          ],
        },
        disableClose: true,
      });
      dialogRef.afterClosed().subscribe(result => {
        
        if (result?.type === 'redirect') {
          this.router.navigate([
            "/" + this.baseWord + "/connectors/datawarehouses-listing",
          ]);
        }
      });
    }
  }

  private openCreateSourcePopUp(){
    if (this.shouldSuppressTrialUi()) {
      return;
    }
    let isTrialSetupStepTwoShown = this.getIsTrialSetupStepShown('isTrialSetupStepTwoShown') // JSON.parse(sessionStorage.getItem(NS_Enums.sessionStorageKeysEnum.isTrialSetupStepTwoShown)) 
    console.log('openCreateSourcePopUp entered if condition this.isTrialPlanActive' , this.isTrialPlanActive );
    console.log('openCreateSourcePopUp entered if condition this.isTrialPlanUsed' , this.isTrialPlanUsed);
    console.log('openCreateSourcePopUp entered if condition isTrialSetupStepTwoShown' , isTrialSetupStepTwoShown );
    console.log('openCreateSourcePopUp entered if condition check !this.isTrialPlanActive && !this.isTrialPlanUsed && !this.isTrialSetupStepTwoShown' , !this.isTrialPlanActive && !this.isTrialPlanUsed && !isTrialSetupStepTwoShown );
    if(!this.isTrialPlanActive && !this.isTrialPlanUsed && !isTrialSetupStepTwoShown){
      console.log('openCreateSourcePopUp entered if condition passed' )
      sessionStorage.setItem(NS_Enums.sessionStorageKeysEnum.isTrialSetupStepTwoShown, JSON.stringify(true));
      const dialogRef = this.dialog.open(CommonDialogComponent, {
        data: {
          title: trialStarterHeading ,
          content: trialStarterConfigureSource,
          image: "successful",
          type: 'trialFlow',
          stepSection: [
            {
              text: 'Configured destination',
              status: 'done',
              colorCss: 'bg-green'
            }, 
            {
              text: 'Configure source',
              status: 'current',
              colorCss: 'bg-blue'
            }
          ],
          buttons: [
            {
              title: "Configure now",
              meta: "/connectors/datasources-listing",
              type: "redirect",
              class: "confirm-dialog-later",
            },
          ],
        },
        disableClose: true,
      });
      dialogRef.afterClosed().subscribe(result => {
        console.log('openCreateSourcePopUp entered popup closed' )
        if (result?.type === 'redirect') {
          this.router.navigate([
            "/" + this.baseWord + "/connectors/datasources-listing",
          ]);
        }
      });
    }
  }

  getIsTrialSetupStepShown(step) {
    const item = sessionStorage.getItem(NS_Enums.sessionStorageKeysEnum[step])
    if (!item) return null;

    try {
      let isTrialSetupStepTwoShown = JSON.parse(item);
      return isTrialSetupStepTwoShown
    } catch (error) {
      console.error('Failed to parse destination filter:', error);
      return null;
    }
  }

  private doWhenTrialExistAndActive(){
    
    let trialEndDateUTC = new Date();
    trialEndDateUTC.setTime(this.getTrialCompanySubscription().nextBillingDate); 
     
    // EXPECTED: If his trial starts today, to avoid difference in days calculation, setUTCHours for todays Date object same as trialEndDateUTC.
    
    let todayUTC = new Date();
    todayUTC.setUTCHours(trialEndDateUTC.getUTCHours(), trialEndDateUTC.getUTCMinutes(), trialEndDateUTC.getUTCSeconds(), trialEndDateUTC.getUTCMilliseconds());

    let daysCount = getDaysCount(trialEndDateUTC.getTime(), todayUTC.getTime());
    let trialMessage = '';
    if(daysCount === 0 || daysCount === 1){
      trialMessage = `Your trial expires today.`;
    } else if (daysCount> 1 ){
      trialMessage = `Your trial expires in ${daysCount} days`;
    }
    this.setTrialState({ isTrialPlanActive:true, isTrialPlanUsed:false, trialMessage: trialMessage, bannerConfig: {
        theme: 'warning',
        title: trialMessage,
        buttonText: 'Upgrade',
        buttonUrl: newPricingRoute
      }
    })
  }

  private doWhenMigrationAnomalyFound(){
    let message = `There seems to be an error, please connect with Support`;
    this.setTrialState({ isTrialPlanActive:false, isTrialPlanUsed:true, trialMessage:message, bannerConfig: {
      theme: 'error',
      title: message,
      buttonText: 'Talk to customer support',
      buttonUrl: customerSupportLink
    }})
  }

  private doWhenTrialExpiredAndPlanNotSubscribed(){
    let message = `Your trial has expired. Please subscribe to a plan to continue using ${capitalize(this.productName)}`;
    this.setTrialState({ isTrialPlanActive:false, isTrialPlanUsed:true, trialMessage:message, bannerConfig: {
      theme: 'error',
      title: message,
      buttonText: 'Upgrade',
      buttonUrl: newPricingRoute
    }})
  }

  private doWhenTrialExpiredAndPlanSubscribed(){
    this.setTrialState({ isTrialPlanActive:false, isTrialPlanUsed:true, trialMessage:''})  
  }

  private doWhenTrialNotExistAndPlanSubscribed(){
    this.setTrialState({ isTrialPlanActive:false, isTrialPlanUsed:false, trialMessage:''})
  }

  checkIfTrialPlanCompanySubscriptionActive(compSubscription){
    
    // showonly any trial as currently active subscription to show it in overview page
    return (includes(['Trial',  NS_SubscriptionService.trialSubscriptionPlanName], compSubscription?.subscriptionPlan?.name )) 
    // showonly active trial as currently active subscription
    //return (subscription.status == NS_SubscriptionService.TRIAL && subscription.nextBillingDate > new Date().getTime()) 
  }

  doWhenCompanySusbcriptionIsSuspended(companySubscription){
    let message = `We need your attention!`;
    this.setTrialState({ isTrialPlanActive:false, isTrialPlanUsed:true, trialMessage:message, bannerConfig: {
      theme: 'error',
      title: message,
      description: `Your payment was declined. Pay dues to start using ${capitalize(this.productName)}!`,
      buttonText: 'Talk to customer support',
      buttonUrl: customerSupportLink
    }})
  } 

  handleErrorDontRedirect(error: any): Promise<any> {
    this.openErrorDialog(error, false);
    return Promise.reject(error);
  }

  private openErrorDialog(error: any, redirect: boolean) {
    let errorMessage;
    let documentURL:string;
    let documentName:string;
    try {
      if( isJsonValid (get(error,['error','errors',0, 'message'], '')) ){
        let errorObject = JSON.parse(error.error.errors[0].message);
        errorMessage = get(errorObject, ['message'], 'OK');
      } else {
        errorMessage = error.error.errors[0].message;
      }
    } catch (e) {
      errorMessage = error.statusText;
    }

    if(includes(errorMessage, 'No changes are scheduled for this subscription.') ){
      errorMessage = 'No changes are scheduled for this subscription.';
    }

    if (errorMessage === 'Forbidden' || errorMessage === 'Token is not valid') {
      errorMessage = 'Token is not valid. logging out..';
    }

    if(error === 'Contact Support' || error === 'Subscribe for payment information'){
      errorMessage = error;
    }
    if(errorMessage === 'OK') {
      errorMessage = 'Oops! Something went wrong'
    }
    if(errorMessage === 'Unknown Error') {
      errorMessage = 'Server Not Responding.'
    }

    // if(errorMessage == "This is an upgrade scenario but the prorated bill needs to be 0 or more. So, cannot upgrade." || includes(errorMessage, "To perform this operation, the current term's invoice")){
    //   errorMessage = 'Upgrade failed. Please retry or contact support'
    // }
    const dialogRef = this.dialog.open(CommonDialogComponent, {
      data: {
        title: 'Alert!',
        content: errorMessage,
        documentURL: documentURL,
        documentName: documentName,
        image: 'error',
        buttons: [
          {
            title: 'OK',
            meta: '/integrations-list',
            type: 'no-change',
            class: 'confirm-dialog-later',
          },
        ],
      },
      disableClose: true,
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (errorMessage === 'Token is not valid. logging out..') {
        this.cacheService.clear();
        
        if (redirect) {
          this.router.navigate(['/']).then();
        }
      }
      if(errorMessage === 'Subscribe for payment information'){
        
      }
      if(errorMessage === 'Not Allowed') {
        this.router.navigate(['/']);
      }
    });
  }

}
